import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import purchaserService from '../services/purchaser.service';
import { setMessage } from './message';
export const fetchPurchaserList = createAsyncThunk(
    'purchaser/fetchPurchaserList',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await purchaserService.getPurchaserList(page, pagesize, search, ordering);
            return response.data; // Assuming response contains { data, count, totalPages, etc. }
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    },
);

export const fetchPurchaserDropdown = createAsyncThunk(
    'purchaser/fetchPurchaserDropdown',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await purchaserService.getPurchaserDropdown(page, pagesize, search, ordering);
            return response.data; // Assuming response contains { data, count, totalPages, etc. }
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    },
);
export const fetchArchivedPurchaserList = createAsyncThunk(
    'purchaser/fetchArchivedPurchaserList',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await purchaserService.getPurchaserArchiveList(page, pagesize, search, ordering);
            return response.data; // Assuming response contains { data, count, totalPages, etc. }
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
            
        }
    },
);
export const createPurchaser = createAsyncThunk(
    'purchaser/createPurchaser',
    async (purchaserData, thunkAPI) => {
        try {
            const response = await purchaserService.addPurchaser(purchaserData);
            return response.data; // Assuming response contains the created die group data
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);
export const updatePurchaser = createAsyncThunk(
    'purchaser/updatePurchaser',
    async ({ id, values }, thunkAPI) => {
        try {
            const response = await purchaserService.updatePurchaser(id, values);
            thunkAPI.dispatch(fetchPurchaserList({ page: 1, limit: 10 })); // Re-fetch list after update
            return response.data; // Return the updated die group data
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const deletePurchaser = createAsyncThunk(
    'purchaser/deletePurchaser',
    async (id, thunkAPI) => {
        try {
            await purchaserService.deletePurchaser(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const unArchivePurchaser = createAsyncThunk(
    'purchaser/unArchivePurchaser',
    async (id, thunkAPI) => {
        try {
            await purchaserService.unArchivePurchaser(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    },
);

const initialState = {
    data: [],
    list: [],
    loading: false,
    totalpage: 0,
    currentPage: 1,
    totalRecord: 0,
    status: 'idle',
    error: null,
    hasMore: true,
};

const purchaserSlice = createSlice({
    name: 'purchaser',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        resetPurchaserList: state => {
            state.list = []; // This will clear the list
        },
        resetCurrentPurchaserPage: state => {
            state.currentPage = 1;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchPurchaserList.pending, state => {
                state.status = 'pending';
                state.loading = true;
            })
            .addCase(fetchPurchaserList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                const fetchedPage = action.payload.page || state.currentPage;
                state.data = action.payload.results.data || []; 
                state.totalRecord = action.payload.count || 0;
                state.totalpage = action.payload.totalPages || 0;
                state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchPurchaserList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });

        builder
            .addCase(fetchPurchaserDropdown.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchPurchaserDropdown.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentPage;
                state.data = action.payload.results.data || []; // Adjust this to match your API response
                state.totalRecord = action.payload.count || 0;
                state.totalpage = action.payload.totalPages || 0;
                state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchPurchaserDropdown.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });


        builder
            .addCase(fetchArchivedPurchaserList.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchArchivedPurchaserList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentPage;
                state.data = action.payload.results.data || []; // Adjust this to match your API response
                state.totalRecord = action.payload.count || 0;
                state.totalpage = action.payload.totalPages || 0;
                state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchArchivedPurchaserList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.data =  []; 
                state.list =  [];  
                state.error = action.error.message;
            });
        builder
            .addCase(createPurchaser.pending, state => {
                state.status = 'loading';
            })
            .addCase(createPurchaser.fulfilled, (state, action) => {
                state.status = 'succeeded';
            })
            .addCase(createPurchaser.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
        builder
            .addCase(updatePurchaser.pending, state => {
                state.status = 'loading';
            })
            .addCase(updatePurchaser.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const updatedGroup = action.payload;
                const index = state.data.findIndex(
                    group => group.id === updatedGroup.id,
                ); // Find the index of the updated group
                if (index !== -1) {
                    state.data[index] = updatedGroup; // Replace the old group with the updated one
                }
            })
            .addCase(updatePurchaser.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(deletePurchaser.pending, state => {
                state.status = 'loading';
            })
            .addCase(deletePurchaser.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
                state.list = state.list.filter(group => group.id !== action.payload); // Remove the deleted group from list
            })
            .addCase(deletePurchaser.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(unArchivePurchaser.pending, state => {
                state.status = 'loading';
            })
            .addCase(unArchivePurchaser.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
            })
            .addCase(unArchivePurchaser.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });
    },
});

export const { resetPurchaserList, resetCurrentPurchaserPage } = purchaserSlice.actions;
const { reducer } = purchaserSlice;
export default reducer;
